import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  LuUsers, LuPlus, LuPencil, LuTrash2, LuBadgeCheck, LuX, LuCheck, LuShield,
} from 'react-icons/lu';

const GENDER_OPTIONS = [
  { value: '',            label: 'Não informado' },
  { value: 'masculino',   label: 'Masculino' },
  { value: 'feminino',    label: 'Feminino' },
  { value: 'outro',       label: 'Outro' },
];

const TYPE_OPTIONS = [
  { value: 'admin',    label: 'Administrador' },
  { value: 'gestor',   label: 'Gestor' },
  { value: 'operador', label: 'Operador' },
];

const emptyForm = {
  username: '', password: '', full_name: '', phone: '', cpf: '',
  address: '', gender: '', birth_date: '', Email: '',
  type_user: 'operador', employee_id: '', company_id: '',
};

function formatCpf(v) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

function formatPhone(v) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10)
    return d.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3').trim().replace(/-$/, '');
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3').trim().replace(/-$/, '');
}

/* ─── Modal de criação / edição ─── */
function UserModal({ editing, departments, companies, onClose, onSaved }) {
  const [form,        setForm]        = useState(editing
    ? {
        username:    editing.user       || '',
        password:    '',
        full_name:   editing.full_name  || '',
        phone:       editing.phone      || '',
        cpf:         editing.cpf        || '',
        address:     editing.address    || '',
        gender:      editing.gender     || '',
        birth_date:  editing.birth_date || '',
        Email:       editing.Email      || '',
        type_user:   editing.type_user  || 'operador',
        employee_id: editing.employee_id != null ? String(editing.employee_id) : '',
        company_id:  editing.company_id  != null ? String(editing.company_id)  : '',
      }
    : emptyForm,
  );
  const [employees,  setEmployees]  = useState([]);
  const [deptSel,    setDeptSel]    = useState(
    editing?.employee?.department?.id ? String(editing.employee.department.id) : ''
  );
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');

  useEffect(() => {
    if (!deptSel) { setEmployees([]); return; }
    api.get(`/departments/${deptSel}/employees`)
      .then((r) => setEmployees(r.data))
      .catch(console.error);
  }, [deptSel]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.full_name.trim()) { setError('Nome é obrigatório'); return; }
    if (!form.username.trim())  { setError('Nome de usuário é obrigatório'); return; }
    if (!editing && !form.password) { setError('Senha é obrigatória para novo usuário'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        employee_id: form.employee_id || null,
        company_id:  form.company_id  || null,
        birth_date:  form.birth_date  || null,
        password:    form.password    || undefined,
      };
      let saved;
      if (editing) {
        const { data } = await api.put(`/users/${editing.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post('/users', payload);
        saved = data;
      }
      onSaved(saved, !!editing);
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.40)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: 'var(--surface-1)', borderRadius: 14, width: '100%', maxWidth: 640,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
          <div style={{ fontWeight: 700, fontSize: 17, color: 'var(--text-1)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <LuBadgeCheck color="var(--accent)" />
            {editing ? 'Editar Usuário' : 'Novo Usuário'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 4 }}>
            <LuX size={18} />
          </button>
        </div>

        <div style={{ padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: 0 }}>
          {error && <div className="alert-custom danger" style={{ marginBottom: 14, fontSize: 13 }}>{error}</div>}

          {/* Seção: Dados pessoais */}
          <div className="form-section-title" style={{ marginBottom: 10 }}>Dados Pessoais</div>
          <div className="resp-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Nome completo *</label>
              <input className="form-control" value={form.full_name}
                onChange={(e) => set('full_name', e.target.value)} placeholder="Nome completo" autoFocus />
            </div>
            <div>
              <label className="form-label">CPF</label>
              <input className="form-control" value={form.cpf}
                onChange={(e) => set('cpf', formatCpf(e.target.value))}
                placeholder="000.000.000-00" />
            </div>
            <div>
              <label className="form-label">Data de nascimento</label>
              <input type="date" className="form-control" value={form.birth_date}
                onChange={(e) => set('birth_date', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Sexo</label>
              <select className="form-control" value={form.gender} onChange={(e) => set('gender', e.target.value)}>
                {GENDER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Telefone</label>
              <input className="form-control" value={form.phone}
                onChange={(e) => set('phone', formatPhone(e.target.value))}
                placeholder="(00) 00000-0000" />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">Endereço</label>
              <input className="form-control" value={form.address}
                onChange={(e) => set('address', e.target.value)} placeholder="Rua, número, bairro, cidade" />
            </div>
          </div>

          {/* Seção: Acesso */}
          <div className="form-section-title" style={{ marginBottom: 10 }}>Acesso ao Sistema</div>
          <div className="resp-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label className="form-label">E-mail</label>
              <input type="email" className="form-control" value={form.Email}
                onChange={(e) => set('Email', e.target.value)} placeholder="email@exemplo.com" />
            </div>
            <div>
              <label className="form-label">Tipo de usuário</label>
              <select className="form-control" value={form.type_user} onChange={(e) => set('type_user', e.target.value)}>
                {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Nome de usuário (login) *</label>
              <input className="form-control" value={form.username}
                onChange={(e) => set('username', e.target.value)} placeholder="login" autoComplete="off" />
            </div>
            <div>
              <label className="form-label">{editing ? 'Nova senha (deixe em branco para manter)' : 'Senha *'}</label>
              <input type="password" className="form-control" value={form.password}
                onChange={(e) => set('password', e.target.value)} placeholder="••••••••" autoComplete="new-password" />
            </div>
          </div>

          {/* Seção: Empresa */}
          <div className="form-section-title" style={{ marginBottom: 10 }}>Empresa</div>
          <div style={{ marginBottom: 14 }}>
            <label className="form-label">Empresa / Filial</label>
            <select className="form-control" value={form.company_id}
              onChange={(e) => set('company_id', e.target.value)}>
              <option value="">— nenhuma —</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.trade_name ? `${c.trade_name} (${c.name})` : c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Seção: Vínculo */}
          <div className="form-section-title" style={{ marginBottom: 10 }}>Vínculo com Funcionário</div>
          <div className="resp-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 4 }}>
            <div>
              <label className="form-label">Departamento</label>
              <select className="form-control" value={deptSel}
                onChange={(e) => { setDeptSel(e.target.value); set('employee_id', ''); }}>
                <option value="">Selecione…</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Funcionário</label>
              <select className="form-control" value={form.employee_id}
                onChange={(e) => set('employee_id', e.target.value)} disabled={!deptSel}>
                <option value="">Nenhum</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 20 }}>
            Vincular permite que este usuário use a tela "Bater Ponto".
          </div>

          {/* Ações */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{
              padding: '9px 20px', border: '1px solid var(--border)', borderRadius: 8,
              background: 'var(--surface-1)', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', color: 'var(--text-2)',
            }}>
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} style={{
              padding: '9px 22px', border: 'none', borderRadius: 8, cursor: 'pointer',
              fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
              background: 'var(--accent)', color: 'var(--on-accent)',
              display: 'flex', alignItems: 'center', gap: 6,
              opacity: saving ? 0.7 : 1,
            }}>
              <LuCheck /> {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Página principal ─── */
export default function UsersAdminPage() {
  const [users,       setUsers]       = useState([]);
  const [departments, setDepartments] = useState([]);
  const [companies,   setCompanies]   = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [modal,       setModal]       = useState(null); // null | 'new' | <user obj>
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      api.get('/users'),
      api.get('/departments'),
      api.get('/companies'),
    ]).then(([u, d, c]) => {
      if (cancelled) return;
      setUsers(u.data);
      setDepartments(d.data);
      setCompanies(c.data);
    }).catch(() => {
      if (!cancelled) setError('Erro ao carregar dados');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const handleSaved = (saved, isEdit) => {
    if (isEdit) {
      setUsers((p) => p.map((u) => (u.id === saved.id ? saved : u)));
    } else {
      setUsers((p) => [...p, saved].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')));
    }
    setModal(null);
    setSuccess(isEdit ? 'Usuário atualizado com sucesso' : 'Usuário criado com sucesso');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Remover o usuário "${u.full_name || u.user}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((p) => p.filter((x) => x.id !== u.id));
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao remover');
    }
  };

  const typeBadge = (type) => {
    const map = {
      admin:    { bg: 'var(--purple-soft)', color: 'var(--danger)', label: 'Admin' },
      gestor:   { bg: 'var(--info-soft)', color: 'var(--info)', label: 'Gestor' },
      operador: { bg: 'var(--surface-3)', color: 'var(--text-2)', label: 'Operador' },
    };
    const s = map[type] || map.operador;
    return (
      <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>
        {s.label}
      </span>
    );
  };

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Usuários</div>
          <div className="page-card-subtitle">Gerencie os usuários e seus vínculos com funcionários</div>
        </div>
        <button className="btn-salvar" onClick={() => setModal('new')}>
          <LuPlus style={{ marginRight: 6 }} /> Novo Usuário
        </button>
      </div>

      {error   && <div className="alert-custom danger"  style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className="alert-custom success" style={{ marginBottom: 12 }}>{success}</div>}

      {loading && <div style={{ color: 'var(--text-3)', fontSize: 13, padding: '16px 0' }}>Carregando…</div>}

      {!loading && users.length === 0 && (
        <div className="empty-state">
          <LuUsers size={32} color="var(--text-3)" style={{ marginBottom: 8 }} />
          <p>Nenhum usuário cadastrado</p>
        </div>
      )}

      {users.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {['Nome', 'Login', 'E-mail', 'Tipo', 'Empresa', 'Funcionário vinculado', ''].map((h) => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: 'var(--text-3)', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '11px 12px' }}>
                    <div style={{ fontWeight: 700, color: 'var(--text-1)' }}>{u.full_name || '—'}</div>
                    {u.birth_date && (
                      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                        {new Date(u.birth_date + 'T12:00:00').toLocaleDateString('pt-BR')}
                        {u.gender ? ` · ${u.gender.charAt(0).toUpperCase() + u.gender.slice(1)}` : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '11px 12px', color: 'var(--text-2)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <LuShield size={11} color="var(--text-3)" />
                      {u.user}
                    </span>
                  </td>
                  <td style={{ padding: '11px 12px', color: 'var(--text-2)' }}>{u.Email || '—'}</td>
                  <td style={{ padding: '11px 12px' }}>{typeBadge(u.type_user)}</td>
                  <td style={{ padding: '11px 12px' }}>
                    {u.company
                      ? <div>
                          <div style={{ fontWeight: 600, color: 'var(--text-1)', fontSize: 12 }}>{u.company.trade_name || u.company.name}</div>
                          {u.company.trade_name && <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{u.company.name}</div>}
                        </div>
                      : <span style={{ color: 'var(--text-3)', fontSize: 12, fontStyle: 'italic' }}>Não vinculada</span>
                    }
                  </td>
                  <td style={{ padding: '11px 12px' }}>
                    {u.employee
                      ? (
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--text-1)', fontSize: 12 }}>{u.employee.full_name}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{u.employee.department?.name || ''}</div>
                        </div>
                      )
                      : <span style={{ color: 'var(--text-3)', fontSize: 12, fontStyle: 'italic' }}>Não vinculado</span>
                    }
                  </td>
                  <td style={{ padding: '11px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => setModal(u)} title="Editar" style={iconBtn('var(--text-1)')}>
                      <LuPencil size={13} />
                    </button>
                    <button onClick={() => handleDelete(u)} title="Remover" style={iconBtn('var(--danger)')}>
                      <LuTrash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <UserModal
          editing={modal === 'new' ? null : modal}
          departments={departments}
          companies={companies}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

const iconBtn = (color) => ({
  background: 'none', border: 'none', cursor: 'pointer', color,
  padding: '4px 6px', borderRadius: 5,
});
